import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Badge, 
  Modal, 
  Form, 
  Alert, 
  Row, 
  Col,
  Tabs,
  Tab,
  InputGroup,
  ProgressBar,
  Accordion
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faKey,
  faPlus,
  faEye,
  faEyeSlash,
  faTrash,
  faCheckCircle,
  faExclamationTriangle,
  faChartLine,
  faCode,
  faCopy,
  faRocket,
  faZap,
  faClipboardCheck,
  faBook,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';

interface APIKey {
  id: string;
  key_name: string;
  key_prefix: string;
  permissions: string[];
  is_active: boolean;
  rate_limit_per_hour: number;
  usage_count: number;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
}

interface UsageAnalytics {
  total_requests: number;
  successful_requests: number;
  success_rate: number;
  active_days: number;
  usage_by_date: Array<{
    date: string;
    requests: number;
    successful_requests: number;
  }>;
  usage_by_endpoint: Array<{
    endpoint: string;
    requests: number;
  }>;
}

const CustomerAPIKeys: React.FC = () => {
  // State management
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [analytics, setAnalytics] = useState<UsageAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<APIKey | null>(null);
  const [newApiKeyData, setNewApiKeyData] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    key_name: '',
    permissions: ['leads:create', 'webhooks:receive'],
    rate_limit_per_hour: 100,
    expires_days: null as number | null
  });

  // Load data on component mount
  useEffect(() => {
    fetchAPIKeys();
    fetchAnalytics();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get('/api/customer-api-keys');
      
      if (response.data.success) {
        setApiKeys(response.data.data.api_keys || []);
      } else {
        setError(response.data.error || 'Failed to fetch API keys');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axiosInstance.get('/api/customer-api-keys/usage-analytics?days=30');
      
      if (response.data.success) {
        setAnalytics(response.data.data.analytics);
      }
    } catch (err: any) {
      console.warn('Failed to fetch analytics:', err);
      // Don't show error for analytics - it's not critical
    }
  };

  const handleCreateKey = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.post('/api/customer-api-keys', formData);
      
      if (response.data.success) {
        setNewApiKeyData(response.data.data.api_key);
        setSuccess('API key created successfully! Save it securely - it will not be shown again.');
        
        // Reset form
        setFormData({
          key_name: '',
          permissions: ['leads:create', 'webhooks:receive'],
          rate_limit_per_hour: 100,
          expires_days: null
        });
        
        // Refresh the list
        fetchAPIKeys();
      } else {
        setError(response.data.error || 'Failed to create API key');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!selectedKey) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.delete(`/api/customer-api-keys/${selectedKey.id}`);
      
      if (response.data.success) {
        setSuccess('API key revoked successfully');
        setShowDeleteModal(false);
        setSelectedKey(null);
        fetchAPIKeys();
      } else {
        setError(response.data.error || 'Failed to revoke API key');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke API key');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Copied to clipboard!');
      setTimeout(() => setSuccess(null), 2000);
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (key: APIKey) => {
    if (!key.is_active) {
      return <Badge bg="secondary">Revoked</Badge>;
    }
    
    if (key.expires_at && new Date(key.expires_at) < new Date()) {
      return <Badge bg="warning">Expired</Badge>;
    }
    
    return <Badge bg="success">Active</Badge>;
  };

  const renderCreateModal = () => (
    <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faKey} className="me-2" />
          Create New API Key
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {newApiKeyData ? (
          <Alert variant="success">
            <Alert.Heading>
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              API Key Created Successfully!
            </Alert.Heading>
            <p>Your new API key has been created. <strong>Save it securely - it will not be shown again.</strong></p>
            
            <div className="bg-light p-3 rounded mb-3" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              <div className="d-flex justify-content-between align-items-center">
                <code>{newApiKeyData}</code>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => copyToClipboard(newApiKeyData)}
                >
                  <FontAwesomeIcon icon={faCopy} />
                </Button>
              </div>
            </div>
            
            <p className="mb-0">
              <strong>Next Steps:</strong>
            </p>
            <ul className="mb-0">
              <li>Save this key in a secure location</li>
              <li>Use it in your Zapier integrations or API calls</li>
              <li>Check the Integration Examples tab for code samples</li>
            </ul>
          </Alert>
        ) : (
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Key Name *</Form.Label>
                  <Form.Control 
                    type="text"
                    placeholder="e.g., Zapier Integration"
                    value={formData.key_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, key_name: e.target.value }))}
                  />
                  <Form.Text className="text-muted">
                    A descriptive name to identify this key
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Rate Limit (per hour)</Form.Label>
                  <Form.Control 
                    type="number"
                    min="1"
                    max="10000"
                    value={formData.rate_limit_per_hour}
                    onChange={(e) => setFormData(prev => ({ ...prev, rate_limit_per_hour: parseInt(e.target.value) }))}
                  />
                  <Form.Text className="text-muted">
                    Maximum requests per hour (1-10,000)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Permissions</Form.Label>
              {['leads:create', 'leads:read', 'webhooks:receive'].map(permission => (
                <Form.Check
                  key={permission}
                  type="checkbox"
                  id={`perm-${permission}`}
                  label={permission}
                  checked={formData.permissions.includes(permission)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData(prev => ({ 
                        ...prev, 
                        permissions: [...prev.permissions, permission] 
                      }));
                    } else {
                      setFormData(prev => ({ 
                        ...prev, 
                        permissions: prev.permissions.filter(p => p !== permission) 
                      }));
                    }
                  }}
                />
              ))}
              <Form.Text className="text-muted">
                Select the permissions this API key should have
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Expiration (optional)</Form.Label>
              <Form.Select 
                value={formData.expires_days || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  expires_days: e.target.value ? parseInt(e.target.value) : null 
                }))}
              >
                <option value="">Never expires</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
                <option value="365">1 year</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Choose when this key should expire (optional)
              </Form.Text>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {newApiKeyData ? (
          <Button 
            variant="primary" 
            onClick={() => {
              setShowCreateModal(false);
              setNewApiKeyData(null);
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleCreateKey}
              disabled={loading || !formData.key_name.trim() || formData.permissions.length === 0}
            >
              {loading ? 'Creating...' : 'Create API Key'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );

  const renderDeleteModal = () => (
    <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Revocation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
          <h5>Are you sure?</h5>
          <p>
            This will permanently revoke the API key <strong>"{selectedKey?.key_name}"</strong>.
          </p>
          <p className="text-danger">
            <small>Any integrations using this key will stop working immediately.</small>
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDeleteKey} disabled={loading}>
          {loading ? 'Revoking...' : 'Revoke Key'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderAnalyticsTab = () => (
    <div>
      {analytics ? (
        <Row>
          <Col md={3}>
            <Card className="text-center mb-3">
              <Card.Body>
                <h3 className="text-primary">{analytics.total_requests}</h3>
                <small className="text-muted">Total Requests</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center mb-3">
              <Card.Body>
                <h3 className="text-success">{analytics.success_rate}%</h3>
                <small className="text-muted">Success Rate</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center mb-3">
              <Card.Body>
                <h3 className="text-info">{analytics.active_days}</h3>
                <small className="text-muted">Active Days</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center mb-3">
              <Card.Body>
                <h3 className="text-warning">{analytics.successful_requests}</h3>
                <small className="text-muted">Successful Requests</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <Alert variant="info">
          <FontAwesomeIcon icon={faChartLine} className="me-2" />
          No usage data available yet. Start using your API keys to see analytics here.
        </Alert>
      )}
    </div>
  );

  const renderIntegrationExamples = () => (
    <div>
      <Alert variant="primary" className="mb-4">
        <FontAwesomeIcon icon={faRocket} className="me-2" />
        <strong>Quick Start:</strong> Use these examples to integrate KairosIQ with Zapier, webhooks, or custom applications.
      </Alert>

      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <FontAwesomeIcon icon={faZap} className="me-2" />
            Zapier Integration
          </Accordion.Header>
          <Accordion.Body>
            <h6>Step-by-Step Setup:</h6>
            <ol>
              <li>Create a new Zap in Zapier</li>
              <li>Choose your trigger app (Google Forms, Facebook Lead Ads, etc.)</li>
              <li>For the action, search for <strong>"Webhooks by Zapier"</strong></li>
              <li>Choose <strong>"POST"</strong> as the method</li>
              <li>Use URL: <code>https://api.kairosiq.ai/api/v1/leads</code></li>
              <li>Add headers:
                <ul>
                  <li><code>Authorization: Bearer YOUR_API_KEY</code></li>
                  <li><code>Content-Type: application/json</code></li>
                </ul>
              </li>
              <li>Map your form fields to the JSON structure (see cURL example below)</li>
              <li>Test your Zap to ensure leads are created successfully</li>
            </ol>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            <FontAwesomeIcon icon={faCode} className="me-2" />
            cURL Example
          </Accordion.Header>
          <Accordion.Body>
            <pre className="bg-light p-3 rounded" style={{ fontSize: '0.9em' }}>
{`curl -X POST https://api.kairosiq.ai/api/v1/leads \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source": "zapier",
    "contact": {
      "first_name": "John",
      "last_name": "Doe",
      "email": "john@example.com",
      "phone": "+1-555-123-4567"
    },
    "property_interest": {
      "type": "buyer",
      "price_range": "400000-600000",
      "location": "Phoenix, AZ",
      "bedrooms": 3,
      "bathrooms": 2
    },
    "lead_source": {
      "platform": "zillow",
      "campaign": "summer_2025"
    },
    "metadata": {
      "urgency": "high",
      "pre_approved": true,
      "notes": "Looking to move in 60 days"
    }
  }'`}
            </pre>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => copyToClipboard(`curl -X POST https://api.kairosiq.ai/api/v1/leads \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source": "zapier",
    "contact": {
      "first_name": "John",
      "last_name": "Doe",
      "email": "john@example.com",
      "phone": "+1-555-123-4567"
    },
    "property_interest": {
      "type": "buyer",
      "price_range": "400000-600000",
      "location": "Phoenix, AZ",
      "bedrooms": 3,
      "bathrooms": 2
    },
    "lead_source": {
      "platform": "zillow",
      "campaign": "summer_2025"
    },
    "metadata": {
      "urgency": "high",
      "pre_approved": true,
      "notes": "Looking to move in 60 days"
    }
  }'`)}
            >
              <FontAwesomeIcon icon={faCopy} className="me-1" />
              Copy cURL
            </Button>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="2">
          <Accordion.Header>
            <FontAwesomeIcon icon={faBook} className="me-2" />
            JavaScript Example
          </Accordion.Header>
          <Accordion.Body>
            <pre className="bg-light p-3 rounded" style={{ fontSize: '0.9em' }}>
{`const response = await fetch('https://api.kairosiq.ai/api/v1/leads', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    source: 'website',
    contact: {
      first_name: 'Jane',
      last_name: 'Smith',
      email: 'jane@example.com',
      phone: '+1-555-987-6543'
    },
    property_interest: {
      type: 'seller',
      price_range: '500000-700000',
      location: 'Scottsdale, AZ'
    }
  })
});

const data = await response.json();
console.log(data);`}
            </pre>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <div className="mt-4">
        <Alert variant="info">
          <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
          Need more help? Check out our <a href="https://docs.kairosiq.ai/api" target="_blank" rel="noopener noreferrer">full API documentation</a> or contact support at api-support@kairosiq.ai
        </Alert>
      </div>
    </div>
  );

  return (
    <div className="customer-api-keys">
      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            API Keys
          </h4>
          <p className="text-muted mb-0">
            Manage API keys for Zapier integrations and external services
          </p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => setShowCreateModal(true)}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Create API Key
        </Button>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultActiveKey="keys" className="mb-4">
        <Tab eventKey="keys" title={
          <span>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            Your API Keys
          </span>
        }>
          {apiKeys.length === 0 ? (
            <Alert variant="info">
              <FontAwesomeIcon icon={faKey} className="me-2" />
              No API keys created yet. Create your first API key to start integrating with Zapier and other services.
            </Alert>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Status</th>
                  <th>Permissions</th>
                  <th>Rate Limit</th>
                  <th>Usage</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td>
                      <strong>{key.key_name}</strong>
                    </td>
                    <td>
                      <code>{key.key_prefix}</code>
                    </td>
                    <td>
                      {getStatusBadge(key)}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {key.permissions.map(perm => (
                          <Badge key={perm} bg="secondary" className="small">
                            {perm}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td>
                      {key.rate_limit_per_hour}/hour
                    </td>
                    <td>
                      <Badge bg="light" text="dark">
                        {key.usage_count} requests
                      </Badge>
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatDate(key.created_at)}
                      </small>
                    </td>
                    <td>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => {
                          setSelectedKey(key);
                          setShowDeleteModal(true);
                        }}
                        disabled={!key.is_active}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        <Tab eventKey="analytics" title={
          <span>
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            Usage Analytics
          </span>
        }>
          {renderAnalyticsTab()}
        </Tab>

        <Tab eventKey="examples" title={
          <span>
            <FontAwesomeIcon icon={faCode} className="me-2" />
            Integration Examples
          </span>
        }>
          {renderIntegrationExamples()}
        </Tab>
      </Tabs>

      {/* Modals */}
      {renderCreateModal()}
      {renderDeleteModal()}
    </div>
  );
};

export default CustomerAPIKeys;